import { supabase } from './supabase';
import { createComponentLogger } from './logger';

const log = createComponentLogger('AuthUtils', 'auth');

export interface AuthUser {
  id: string;
  email: string;
  organizationId?: string;
  role?: string;
}

/**
 * Get the current authenticated user with their profile information
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      log.error('Authentication error', { error: authError?.message || 'Unknown auth error' });
      return null;
    }

    // Get user profile with organization info
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('id, email, organization_id, roles')
      .eq('id', user.id)
      .single();

    if (profileError) {
      log.error('Profile retrieval error', { error: profileError?.message || 'Unknown profile error' });
      // Return basic user info even if profile fetch fails
      return {
        id: user.id,
        email: user.email || ''
      };
    }

    return {
      id: user.id,
      email: user.email || profile.email || '',
      organizationId: profile.organization_id,
      role: profile.roles?.[0]
    };
  } catch (error) {
    log.error('Error getting current user', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  return !!user;
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) {
    log.error('Error signing out', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
}